import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { PROGRESS_TYPES, PROGRESS_OUTCOMES, OUTCOME_STYLES } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Paperclip, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const emptyEntry = {
  date: today(), type: "contact", summary: "", outcome: "none",
  next_action: "", next_action_due: "", attachment_url: "",
};

const EntryRow = ({ e, canManage, onDelete, t }) => (
  <li className="relative pl-6 pb-4 last:pb-0" data-testid={`progress-entry-${e.id}`}>
    <span className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-orange-500 ring-4 ring-orange-50"></span>
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
      <span className="font-mono font-semibold text-slate-700">{e.date}</span>
      <span className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 font-semibold text-slate-600">{t(e.type)}</span>
      {e.outcome && (
        <span className={`px-1.5 py-0.5 rounded-full border font-semibold ${OUTCOME_STYLES[e.outcome]}`} data-testid={`progress-outcome-${e.id}`}>
          {t(e.outcome)}
        </span>
      )}
      <span>· {e.author_name || "—"}</span>
      {canManage && (
        <button
          onClick={() => onDelete(e.id)}
          data-testid={`delete-progress-${e.id}`}
          className="ml-auto p-1 text-slate-400 hover:text-rose-600"
        ><Trash2 className="w-3.5 h-3.5" /></button>
      )}
    </div>
    <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{e.summary}</p>
    {e.next_action && (
      <p className="text-xs text-slate-600 mt-1 flex items-center gap-1.5">
        <ArrowRightCircle className="w-3.5 h-3.5 text-orange-500" />
        {e.next_action}{e.next_action_due ? ` · ${e.next_action_due}` : ""}
      </p>
    )}
    {e.attachment_url && (
      <a
        href={e.attachment_url}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-orange-600 hover:underline mt-1 inline-flex items-center gap-1"
      ><Paperclip className="w-3 h-3" /> {t("attachment_link")}</a>
    )}
  </li>
);

export const ProgressLog = ({ dealId }) => {
  const { t } = useLang();
  const { user, can } = useAuth();
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyEntry);
  const [showForm, setShowForm] = useState(false);
  const canWrite = can("progress:write");

  const load = useCallback(async () => {
    const res = await api.get(`/deals/${dealId}/progress`);
    setEntries(res.data);
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.summary.trim()) { toast.error(t("summary_required")); return; }
    try {
      const payload = {
        ...form,
        outcome: form.outcome === "none" ? null : form.outcome,
        next_action: form.next_action || null,
        next_action_due: form.next_action_due || null,
        attachment_url: form.attachment_url || null,
      };
      const res = await api.post(`/deals/${dealId}/progress`, payload);
      setEntries((prev) => [res.data, ...prev]);
      setForm({ ...emptyEntry, date: today() });
      setShowForm(false);
      toast.success(t("progress_added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/progress/${id}`);
      setEntries((prev) => prev.filter((x) => x.id !== id));
      toast.success(t("progress_deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="col-span-2 border-t border-slate-200 pt-4 space-y-3" data-testid="progress-log-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900">{t("progress_log")}</h4>
        {canWrite && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowForm((v) => !v)}
            data-testid="toggle-progress-form-button"
            className="h-7 gap-1.5 border-slate-300 text-xs"
          ><Plus className="w-3.5 h-3.5" /> {t("add_progress")}</Button>
        )}
      </div>

      {canWrite && showForm && (
        <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="progress-quick-add">
          <div>
            <Label className="text-[11px]">{t("progress_date")}</Label>
            <Input type="date" className="h-8" data-testid="progress-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px]">{t("progress_type")}</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="progress-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROGRESS_TYPES.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[11px]">{t("progress_summary")}</Label>
            <Input className="h-8" data-testid="progress-summary-input" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px]">{t("progress_outcome")} <span className="text-slate-400">({t("optional")})</span></Label>
            <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="progress-outcome-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {PROGRESS_OUTCOMES.map((x) => <SelectItem key={x} value={x}>{t(x)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">{t("next_action_due")}</Label>
            <Input type="date" className="h-8" data-testid="progress-next-due-input" value={form.next_action_due} onChange={(e) => setForm({ ...form, next_action_due: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px]">{t("next_action")}</Label>
            <Input className="h-8" data-testid="progress-next-action-input" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px]">{t("attachment_link")}</Label>
            <Input className="h-8" placeholder="https://" data-testid="progress-attachment-input" value={form.attachment_url} onChange={(e) => setForm({ ...form, attachment_url: e.target.value })} />
          </div>
          <div className="col-span-2 flex justify-end">
            <Button size="sm" onClick={add} data-testid="save-progress-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="progress-empty">{t("no_progress")}</p>
      ) : (
        <ul className="border-l border-slate-200 ml-1 pl-3 max-h-56 overflow-y-auto crm-scroll" data-testid="progress-timeline">
          {entries.map((e) => (
            <EntryRow
              key={e.id}
              e={e}
              t={t}
              canManage={canWrite && (e.author_id === user?.user_id || user?.role === "admin")}
              onDelete={remove}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProgressLog;
